import {
  SchedulerSidebarCrewMember,
  SchedulerSidebarEquipmentMember,
} from '@contexts/scheduler/sidebar/types';
import {
  CurrentSchedule,
  RelatedMultischedules,
  RequestScheduleEquipmentAttributes,
  RequestScheduleMembersAttributes,
} from '@services/schedules/internal/types';
import { IsScheduleDataUnchangedValues, MultiDayOptions } from './types';
import { toZonedTime } from 'date-fns-tz';
import { toTenantDate } from '@utils/timeZone';
import {
  addDays,
  differenceInCalendarDays,
  isSaturday,
  isSunday,
  parseISO,
  startOfDay,
} from 'date-fns';
import { MAX_MULTI_DAYS } from './constants';

const getInitialCrewMembers = (
  curr: SchedulerSidebarCrewMember[],
  prev?: SchedulerSidebarCrewMember[],
): RequestScheduleMembersAttributes[] =>
  curr
    .filter((c) => c.userId)
    .map((v) => {
      const prevEl = prev?.find((el) => el.userId === v.userId);
      if (prevEl)
        return { id: prevEl.id, userId: prevEl.userId } as RequestScheduleMembersAttributes;
      return { userId: v.userId } as RequestScheduleMembersAttributes;
    });

const getRemovedCrewMembers = (
  curr: SchedulerSidebarCrewMember[],
  prev?: SchedulerSidebarCrewMember[],
): RequestScheduleMembersAttributes[] => {
  return (
    prev
      ?.filter((el) => {
        return !curr.some((m) => m.userId === el.userId);
      })
      .map((el) => ({ id: el.id || '', userId: el.userId, _destroy: '1' })) || []
  );
};

const getInitialEquipmentMembers = (
  curr: SchedulerSidebarEquipmentMember[],
  prev?: SchedulerSidebarEquipmentMember[],
): RequestScheduleEquipmentAttributes[] =>
  curr
    .filter((c) => c.assetId)
    .map((v) => {
      const prevEl = prev?.find((el) => el.assetId === v.assetId);
      if (prevEl)
        return { id: prevEl.id, assetId: prevEl.assetId } as RequestScheduleEquipmentAttributes;
      return { assetId: v.assetId } as RequestScheduleEquipmentAttributes;
    });

const getRemovedEquipmentMembers = (
  curr: SchedulerSidebarEquipmentMember[],
  prev?: SchedulerSidebarEquipmentMember[],
): RequestScheduleEquipmentAttributes[] => {
  return (
    prev
      ?.filter((el) => {
        return !curr.some((m) => m.assetId === el.assetId);
      })
      .map((el) => ({ id: el.id || '', assetId: el.assetId, _destroy: '1' })) || []
  );
};

export const parseScheduleMembersAttributesToRequest = (
  crewMembers: SchedulerSidebarCrewMember[],
  prev?: SchedulerSidebarCrewMember[],
): RequestScheduleMembersAttributes[] => {
  return [...getInitialCrewMembers(crewMembers, prev), ...getRemovedCrewMembers(crewMembers, prev)];
};

export const parseScheduleEquipmentAttributesToRequest = (
  equipmentMembers: SchedulerSidebarEquipmentMember[],
  prev?: SchedulerSidebarEquipmentMember[],
): RequestScheduleEquipmentAttributes[] => {
  return [
    ...getInitialEquipmentMembers(equipmentMembers, prev),
    ...getRemovedEquipmentMembers(equipmentMembers, prev),
  ];
};

const isSameInstant = (a: string, b: string) => parseISO(a).getTime() === parseISO(b).getTime();

export const isScheduleDataUnchanged = (
  values: IsScheduleDataUnchangedValues,
  currentSchedule: CurrentSchedule,
) => {
  if (!currentSchedule) return false;

  const statusChangedToDraft =
    values.status === 'draft' && values.status !== currentSchedule.status;
  return (
    !statusChangedToDraft &&
    isSameInstant(values.startsAt, currentSchedule.startsAt) &&
    isSameInstant(values.endsAt, currentSchedule.endsAt) &&
    values.foremanId === currentSchedule.foreman?.id &&
    values.noteContent === (currentSchedule?.task?.note?.content ?? '')
  );
};

export const isSkippedDay = (day: Date, { skipSaturday, skipSunday }: MultiDayOptions) =>
  Boolean((skipSaturday && isSaturday(day)) || (skipSunday && isSunday(day)));

export const getMultiDayDates = (start: Date, days: number, options: MultiDayOptions) => {
  const total = Math.min(Math.max(days, 1), MAX_MULTI_DAYS);
  const dates = [startOfDay(start)];
  let day = dates[0];
  while (dates.length < total) {
    day = addDays(day, 1);
    if (!isSkippedDay(day, options)) dates.push(day);
  }
  return dates;
};

export const parseMultiDaySchedules = (
  startsAt: string,
  endsAt: string,
  days: number,
  options: MultiDayOptions,
) => {
  const start = toTenantDate(startsAt);
  const end = toTenantDate(endsAt);

  return getMultiDayDates(start, days, options).map((day) => {
    const offset = differenceInCalendarDays(day, start);
    return {
      startsAt: addDays(start, offset).toISOString(),
      endsAt: addDays(end, offset).toISOString(),
    };
  });
};

export const getNextWorkDates = (after: Date, count: number, options: MultiDayOptions) => {
  const dates: Date[] = [];
  let day = startOfDay(after);
  while (dates.length < Math.min(count, MAX_MULTI_DAYS)) {
    day = addDays(day, 1);
    if (!isSkippedDay(day, options)) dates.push(day);
  }
  return dates;
};

export const getSeriesDays = ({
  relatedMultischedules,
  currentScheduleId,
  startsAt,
  zone,
}: {
  relatedMultischedules: RelatedMultischedules;
  currentScheduleId?: string;
  startsAt?: string;
  zone: string;
}) => {
  const toDay = (value: string) => startOfDay(toZonedTime(parseISO(value), zone));
  const days = Object.values(relatedMultischedules || {})
    .flat()
    .filter((el) => el.id !== currentScheduleId)
    .map((el) => toDay(el.startsAt));
  if (startsAt) days.push(toDay(startsAt));
  return days.sort((a, b) => a.getTime() - b.getTime());
};

export const getSeriesLastDay = (params: Parameters<typeof getSeriesDays>[0]) =>
  getSeriesDays(params).at(-1) || null;

export const getExtensionDates = ({
  count,
  options,
  ...params
}: Parameters<typeof getSeriesDays>[0] & { count: number; options: MultiDayOptions }) => {
  const lastDay = getSeriesLastDay(params);
  if (!lastDay || count < 1) return [];
  return getNextWorkDates(lastDay, count, options);
};

export const parseSchedulesForDates = (
  startsAt: string,
  endsAt: string,
  dates: Date[],
  zone: string,
) => {
  const start = toTenantDate(startsAt);
  const end = toTenantDate(endsAt);
  const zonedStart = startOfDay(toZonedTime(start, zone));

  return dates.map((day) => {
    const offset = differenceInCalendarDays(day, zonedStart);
    return {
      startsAt: addDays(start, offset).toISOString(),
      endsAt: addDays(end, offset).toISOString(),
    };
  });
};
